import clsx from 'clsx';
import { format } from 'date-fns';
import { ComponentPropsWithoutRef, FC, KeyboardEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Icon, getFileIconNameByExtension } from '../../UI';
import { useViewFile } from '../hooks';
import { File as GQLFile } from '../../generated/graphql';
import { FileOptions, FileOptionsProps } from './FileOptions';

export interface FileListitemProps extends Pick<ComponentPropsWithoutRef<'div'>, 'className'>, FileOptionsProps {
  metadata?: React.ReactNode;
  subtitle?: false;
  hideIcon?: boolean;
  disableHoverBackground?: boolean;
  /** When true, clicking the row opens the file in a new tab (a viewer). */
  openOnClick?: boolean;
  /** When provided (with `openOnClick`), called instead of opening a new tab -- e.g. to open a DocumentViewer. */
  onOpenFile?: (file: GQLFile) => void;
}

export const FileListItem: FC<FileListitemProps> = ({
  file,
  tenantID,
  subtitle,
  itemID,
  onDelete,
  className,
  onCancelDelete,
  onDeleteSuccess,
  onDownloadFailure,
  onBeforeDeleteFile,
  onDeleteRollbackFailure,
  fileAttachmentID,
  metadata,
  allowDeleteFile,
  shouldRefreshDownloadURL,
  showPrimaryDownloadAction,
  deleteModalClassName,
  actionsVariant,
  hideIcon,
  disableHoverBackground,
  openOnClick,
  onOpenFile,
}) => {
  const { t: tFile } = useTranslation('file');
  const { viewFile } = useViewFile(tenantID, file, { shouldRefreshDownloadURL: shouldRefreshDownloadURL ?? true });

  const handleOpen = () => {
    if (!openOnClick) return;
    if (onOpenFile) onOpenFile(file);
    else void viewFile();
  };

  const openInteractionProps = openOnClick
    ? {
        role: 'button',
        tabIndex: 0,
        'aria-label': tFile('view-file-label'),
        onClick: handleOpen,
        onKeyDown: (event: KeyboardEvent<HTMLDivElement>) => {
          // Only act when the row itself is focused; otherwise Enter/Space on a
          // nested control (Download/Delete, the sources <summary>) would both
          // open the viewer and have its own activation cancelled.
          if (event.target !== event.currentTarget) return;
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            handleOpen();
          }
        },
      }
    : {};

  return (
    <div
      className={clsx(
        'flex rounded-md [&_.click-anchor-reference-element-trigger]:grid [&_.click-anchor-reference-element-trigger]:place-content-center',
        !disableHoverBackground && 'hover-background-file hover:cursor-pointer',
        openOnClick && 'cursor-pointer',
        className
      )}
      {...openInteractionProps}
    >
      {!hideIcon && <Icon className="self-start" icon={getFileIconNameByExtension(file.name.split('.')[1])} />}
      <div className={clsx('flex min-w-0 flex-1 flex-col items-baseline justify-center', hideIcon ? 'mr-4' : 'mx-4')}>
        <p className="w-full max-w-md truncate text-lg font-normal text-white">{file.name}</p>
        {subtitle !== false && (
          <p className="text-sm font-normal text-darkMode-gray-light">
            <span className="text-darkMode-gray-light/70">{tFile('date-added-file-label')} </span>{' '}
            {format(new Date(file.createdAt), 'MMM d, yyyy')} {tFile('added-by-file-label')} {file.createdBy.givenName}{' '}
            {file.createdBy.familyName}
          </p>
        )}
        {/* Metadata can contain its own controls (e.g. expandable sources); don't let those clicks open the viewer. */}
        {metadata && <div onClick={(event) => openOnClick && event.stopPropagation()}>{metadata}</div>}
      </div>
      {/* Row actions must not trigger the row's open-on-click viewer. */}
      <div onClick={(event) => openOnClick && event.stopPropagation()}>
        <FileOptions
          allowDeleteFile={allowDeleteFile}
          file={file}
          itemID={itemID}
          tenantID={tenantID}
          onDelete={onDelete}
          onBeforeDeleteFile={onBeforeDeleteFile}
          onDeleteRollbackFailure={onDeleteRollbackFailure}
          onCancelDelete={onCancelDelete}
          onDeleteSuccess={onDeleteSuccess}
          onDownloadFailure={onDownloadFailure}
          shouldRefreshDownloadURL={shouldRefreshDownloadURL}
          showPrimaryDownloadAction={showPrimaryDownloadAction}
          fileAttachmentID={fileAttachmentID}
          deleteModalClassName={deleteModalClassName}
          actionsVariant={actionsVariant}
        />
      </div>
    </div>
  );
};
